import { Container, Graphics } from 'pixi.js';
import { ARENA_TILE } from './render';
import type { Mover } from '../nav/mover';

/** Just the fields the overlay heatmaps read — satisfied by any nav build. */
export interface NavHeatSource { distToSafe: Float64Array; openness: Float32Array; }

/**
 * Navigation debug overlay (Movement spec §9) — built before the AI, because nothing
 * works without visibility. Toggle with a dev key. Draws, per agent, the path polyline
 * and current waypoint plus the heading arrow (moveDir), and optionally the
 * distance-to-safe Dijkstra field as a heatmap so the retreat gradient is legible.
 *
 * If the traveled line (where the sprite actually is) diverges wildly from the green
 * path, the mover is broken — that's the whole point of drawing all three.
 */

export interface DebugAgent { mover: Mover; }

export interface ArenaDebug {
  layer: Container;
  enabled: boolean;
  toggle(): boolean;
  cycleHeatmap(): void;
  redraw(agents: DebugAgent[]): void;
  destroy(): void;
}

type Heat = 'none' | 'distToSafe' | 'openness';

export function createArenaDebug(nav: NavHeatSource, w: number): ArenaDebug {
  const layer = new Container();
  layer.visible = false;
  const heat = new Graphics();
  const paths = new Graphics();
  layer.addChild(heat, paths);
  let enabled = false;
  let mode: Heat = 'none';

  const drawHeat = () => {
    heat.clear();
    if (mode === 'none') return;
    const field = mode === 'distToSafe' ? nav.distToSafe : nav.openness;
    // Normalise finite values to 0..1 for tinting.
    let max = 0;
    for (let i = 0; i < field.length; i++) if (Number.isFinite(field[i]) && field[i] > max) max = field[i];
    if (max <= 0) max = 1;
    for (let i = 0; i < field.length; i++) {
      const v = field[i];
      if (!Number.isFinite(v)) continue;
      const t = v / max;                        // near = 0, far = 1
      const x = i % w, y = (i - (i % w)) / w;
      // Blue (near/safe) → red (far/exposed).
      const r = Math.round(255 * t), b = Math.round(255 * (1 - t));
      heat.rect(x * ARENA_TILE, y * ARENA_TILE, ARENA_TILE, ARENA_TILE).fill({ color: (r << 16) | b, alpha: 0.35 });
    }
  };

  return {
    layer,
    get enabled() { return enabled; },
    toggle() { enabled = !enabled; layer.visible = enabled; if (enabled) drawHeat(); return enabled; },
    cycleHeatmap() {
      mode = mode === 'none' ? 'distToSafe' : mode === 'distToSafe' ? 'openness' : 'none';
      if (enabled) drawHeat();
    },
    redraw(agents) {
      if (!enabled) return;
      paths.clear();
      for (const a of agents) {
        const m = a.mover;
        const wp = m.target();
        // Green path from the agent through its remaining waypoints.
        if (wp) {
          paths.moveTo(m.x * ARENA_TILE, m.y * ARENA_TILE);
          paths.lineTo(wp.x * ARENA_TILE, wp.y * ARENA_TILE)
            .stroke({ color: 0x4ade80, width: 1.5, alpha: 0.9 });
          paths.circle(wp.x * ARENA_TILE, wp.y * ARENA_TILE, 3).fill({ color: 0x4ade80, alpha: 0.9 });
        }
        // Blue heading arrow (moveDir).
        const h = m.heading();
        if (h) {
          const ax = m.x * ARENA_TILE, ay = m.y * ARENA_TILE;
          paths.moveTo(ax, ay).lineTo(ax + h.x * ARENA_TILE, ay + h.y * ARENA_TILE)
            .stroke({ color: 0x60a5fa, width: 1.5, alpha: 0.95 });
        }
      }
    },
    destroy() { layer.destroy({ children: true }); },
  };
}
